/**
 * useLiveFeed — polls /api/v1/live/feed every 60s and exposes a single
 * `currentEvent` that rotates through the feed every 4s. When the feed
 * is empty (no recent activity), the hook keeps cycling the LAST received
 * batch so the ticker never goes blank.
 *
 * Returns: { currentEvent: { id, kind, message, amount, createdAt } | null,
 *            count: number }
 *
 * Used by the TopBar "Live" indicator (LiveTicker component).
 */
import { useEffect, useState, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const POLL_MS    = 60000;  // refetch the feed every 60 seconds
const ROTATE_MS  = 4000;   // show each event for 4 seconds

/** Sentinel raised when the backend doesn't expose /live/feed yet (e.g. older
 *  production deploy). Lets the caller fall back to "Live · synced X ago"
 *  instead of showing alarming red "feed paused" copy. */
class FeedUnavailable extends Error {
  constructor(status) { super(`live/feed unavailable (HTTP ${status})`); this.name = 'FeedUnavailable'; this.status = status; }
}

async function fetchFeed(controller) {
  // Match the canonical auth pattern used by every other v2 fetcher
  // (useHomeData, useFansData, etc.): prefer the dashboard-user JWT
  // stored at login, fall back to the build-time API key for local dev.
  // Sending neither produced 401 from the production backend.
  const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
  const apiKey = process.env.REACT_APP_API_KEY;
  const headers = {};
  if (token)       headers.Authorization = `Bearer ${token}`;
  else if (apiKey) headers["X-API-Key"]  = apiKey;
  const res = await fetch(`${API_BASE}/live/feed`, {
    headers,
    signal: controller.signal,
  });
  // 404 / 501 → backend doesn't have the endpoint yet (older deploy).
  // Treat as "feature not available", not an error worth alarming the CEO.
  if (res.status === 404 || res.status === 501) throw new FeedUnavailable(res.status);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (!json || !json.success || !json.data || !Array.isArray(json.data.events)) {
    throw new Error("malformed live feed envelope");
  }
  return json.data.events;
}

export function useLiveFeed() {
  const [events, setEvents] = useState([]);
  // "loading" → initial fetch hasn't returned yet
  // "ok"      → got events successfully
  // "unavailable" → backend doesn't have /live/feed (older deploy) — show normal idle copy
  // "error"   → real fetch failure worth surfacing
  const [status, setStatus] = useState("loading");
  const [lastError, setLastError] = useState(null);
  const [idx, setIdx] = useState(0);
  const eventsRef = useRef([]);

  // Polling effect — refresh the feed periodically. On error keep prior list.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let stopped = false;
    let timer = null;
    let controller = new AbortController();

    const tick = async () => {
      controller = new AbortController();
      try {
        const fresh = await fetchFeed(controller);
        if (stopped) return;
        if (fresh && fresh.length > 0) {
          setEvents(fresh);
          eventsRef.current = fresh;
        }
        // If fetch returned empty, keep showing the previous feed (per CEO directive
        // 2026-05-07: "if nothing to show, repeat old"). eventsRef preserves them.
        setStatus("ok");
        setLastError(null);
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (e?.name === "FeedUnavailable") {
          // Backend doesn't have /live/feed (older deploy). Endpoint isn't
          // going to magically appear — stop polling.
          setStatus("unavailable");
          setLastError(e.message);
          if (timer) { clearInterval(timer); timer = null; }
          return;
        }
        // Any other failure (auth, network, 5xx, malformed envelope). The
        // live ticker is a nice-to-have, not a critical signal — never
        // surface alarming copy to the CEO. Log for engineers, degrade to
        // the same quiet "synced X ago" idle state. Keep polling so the
        // ticker recovers automatically when the issue clears.
        // eslint-disable-next-line no-console
        console.warn("[useLiveFeed] fetch failed:", e.message);
        setStatus("unavailable");
        setLastError(e.message);
      }
    };

    const start = () => {
      stop();
      tick();
      timer = setInterval(tick, POLL_MS);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
      try { controller.abort(); } catch (_) { /* ignore */ }
    };
    const onVisibility = () => {
      if (document.hidden) stop(); else start();
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Rotation effect — cycle through events every 4 seconds. Loops infinitely
  // even when no new feed arrives.
  useEffect(() => {
    if (events.length === 0) return undefined;
    const t = setInterval(() => {
      setIdx(i => (i + 1) % events.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [events.length]);

  const currentEvent = events.length > 0 ? events[idx % events.length] : null;
  return { currentEvent, count: events.length, status, lastError };
}
